/**
 * AudioWorklet piano synth — voices render on Chromium's native audio thread.
 * Loaded via Blob URL so Vite / Electron / Capacitor share one path.
 */

export const PIANO_SYNTH_WORKLET_NAME = "piano-synth-processor";

/** Worklet processor source (AudioWorkletGlobalScope). */
export const PIANO_SYNTH_WORKLET_SOURCE = `
const TWO_PI = Math.PI * 2;
const A4 = 440;
const A4_MIDI = 69;

function midiToHz(m) {
  return A4 * Math.pow(2, (m - A4_MIDI) / 12);
}

class Voice {
  constructor(midi, velocity, sampleRate) {
    this.midi = midi;
    this.freq = midiToHz(midi);
    this.phase = 0;
    this.phase2 = 0;
    this.amp = Math.max(0.05, Math.min(1, velocity / 127)) * 0.48;
    this.env = 0;
    this.state = 'attack';
    // Soft attack avoids click/blast on small OS buffers (esp. Electron).
    this.attack = Math.max(1, Math.floor(sampleRate * 0.004));
    this.decay = Math.max(1, Math.floor(sampleRate * 0.10));
    this.sustain = 0.5;
    this.release = Math.max(1, Math.floor(sampleRate * 0.14));
    this.relFrom = 0;
    this.t = 0;
  }
  releaseNow() {
    this.state = 'release';
    this.relFrom = this.env;
    this.t = 0;
  }
  tick() {
    if (this.state === 'attack') {
      this.t += 1;
      this.env = this.amp * (this.t / this.attack);
      if (this.t >= this.attack) { this.state = 'decay'; this.t = 0; }
    } else if (this.state === 'decay') {
      this.t += 1;
      const p = this.t / this.decay;
      this.env = this.amp * (1 - p * (1 - this.sustain));
      if (this.t >= this.decay) { this.state = 'sustain'; this.env = this.amp * this.sustain; }
    } else if (this.state === 'sustain') {
      this.env = this.amp * this.sustain;
    } else if (this.state === 'release') {
      this.t += 1;
      this.env = this.relFrom * (1 - this.t / this.release);
      if (this.t >= this.release || this.env <= 0.0001) return null;
    }
    const inc = TWO_PI * this.freq / sampleRate;
    this.phase += inc;
    this.phase2 += inc * 2;
    if (this.phase > TWO_PI) this.phase -= TWO_PI;
    if (this.phase2 > TWO_PI) this.phase2 -= TWO_PI;
    // triangle-ish + soft octave partial
    const tri = 2 * Math.abs(2 * (this.phase / TWO_PI - Math.floor(this.phase / TWO_PI + 0.5))) - 1;
    const sine2 = Math.sin(this.phase2) * 0.14;
    return (tri * 0.86 + sine2) * this.env;
  }
}

class PianoSynthProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this.voices = new Map();
    this.master = 0.75;
    this.muted = false;
    this.sustain = false;
    this.held = new Set();
    this.port.onmessage = (e) => this.onMsg(e.data);
  }
  onMsg(msg) {
    if (!msg || !msg.type) return;
    if (msg.type === 'noteOn') {
      const m = msg.midi | 0;
      this.voices.set(m, new Voice(m, msg.velocity ?? 100, sampleRate));
      this.held.delete(m);
    } else if (msg.type === 'noteOff') {
      const m = msg.midi | 0;
      if (this.sustain) { this.held.add(m); return; }
      const v = this.voices.get(m);
      if (v) v.releaseNow();
    } else if (msg.type === 'sustain') {
      this.sustain = !!msg.down;
      if (!this.sustain) {
        for (const m of this.held) {
          const v = this.voices.get(m);
          if (v) v.releaseNow();
        }
        this.held.clear();
      }
    } else if (msg.type === 'master') {
      this.master = Math.max(0, Math.min(1, msg.value ?? 0.75));
    } else if (msg.type === 'mute') {
      this.muted = !!msg.value;
    } else if (msg.type === 'panic') {
      this.voices.clear();
      this.held.clear();
    }
  }
  process(_inputs, outputs) {
    const out = outputs[0][0];
    const ch1 = outputs[0][1];
    if (!out) return true;
    const gain = this.muted ? 0 : this.master;
    for (let i = 0; i < out.length; i++) {
      let s = 0;
      const dead = [];
      for (const [midi, v] of this.voices) {
        const sample = v.tick();
        if (sample == null) dead.push(midi);
        else s += sample;
      }
      for (let d = 0; d < dead.length; d++) this.voices.delete(dead[d]);
      // soft clip — gentle so chords don't slam
      s = Math.tanh(s * 0.95) * gain;
      if (!Number.isFinite(s)) s = 0;
      out[i] = s;
      if (ch1) ch1[i] = s;
    }
    return true;
  }
}

registerProcessor('${PIANO_SYNTH_WORKLET_NAME}', PianoSynthProcessor);
`;
